import { useEffect, useMemo, useState } from "react";
import { BookUser, Lock, Loader2, Trash2, Plus, Phone, Mail, MessageSquarePlus, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inp = "bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60 w-full";
const CAT_LABEL = { council: "Council", state_agency: "State agency", partner: "Partner / panel", team: "Team", other: "Other" };
const CAT_COLOR = { council: "border-gold-500/50 text-gold-300", state_agency: "border-sky-400/50 text-sky-300", partner: "border-emerald-400/50 text-emerald-300", team: "border-white/30 text-white/70", other: "border-white/15 text-white/40" };

function AddForm({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", organisation: "", role: "", state: "", phone: "", email: "", category: "council" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/contacts", f);
      setF({ name: "", organisation: "", role: "", state: "", phone: "", email: "", category: "council" });
      setOpen(false); onDone();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not save the contact."); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="contact-add-toggle"
      className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 px-5 py-2.5 text-sm font-medium hover:bg-gold-400">
      <Plus className="h-4 w-4" /> Add contact
    </button>
  );
  return (
    <form onSubmit={submit} className="border border-gold-500/25 bg-navy-900/50 p-5 space-y-3" data-testid="contact-add-form">
      <Mono className="text-[0.6rem] text-gold-400">New contact</Mono>
      <div className="grid sm:grid-cols-3 gap-3">
        <input value={f.name} onChange={set("name")} placeholder="Name *" required className={inp} data-testid="contact-name-input" />
        <input value={f.organisation} onChange={set("organisation")} placeholder="Organisation" className={inp} data-testid="contact-org-input" />
        <input value={f.role} onChange={set("role")} placeholder="Role / title" className={inp} data-testid="contact-role-input" />
        <input value={f.phone} onChange={set("phone")} placeholder="Phone" className={inp} data-testid="contact-phone-input" />
        <input value={f.email} onChange={set("email")} placeholder="Email" type="email" className={inp} data-testid="contact-email-input" />
        <div className="flex gap-2">
          <input value={f.state} onChange={set("state")} placeholder="State" maxLength={3} className={`${inp} w-20`} data-testid="contact-state-input" />
          <select value={f.category} onChange={set("category")} className={inp} data-testid="contact-category-select">
            {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      {err && <p className="text-amber-300 text-xs" data-testid="contact-add-error">{err}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="contact-add-submit"
          className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 px-5 py-2.5 text-sm font-medium hover:bg-gold-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save contact
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-white/40 hover:text-white text-sm px-3">Cancel</button>
      </div>
    </form>
  );
}

function ContactCard({ c, onDelete, onNote }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try { await onNote(c.id, note.trim()); setNote(""); } finally { setBusy(false); }
  };
  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-4" data-testid={`contact-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-heading text-lg">{c.name}</span>
            <Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${CAT_COLOR[c.category] || CAT_COLOR.other}`}>{CAT_LABEL[c.category] || c.category}</Mono>
            {c.state && <Mono className="text-[0.5rem] text-white/40">{c.state}</Mono>}
          </div>
          <p className="text-white/50 text-xs font-light mt-1">{[c.organisation, c.role].filter(Boolean).join(" · ") || "—"}</p>
          <div className="flex items-center gap-4 mt-2 text-xs">
            {c.phone && <a href={`tel:${c.phone}`} className="text-gold-300 hover:text-gold-200 inline-flex items-center gap-1.5"><Phone className="h-3 w-3" /> {c.phone}</a>}
            {c.email && <a href={`mailto:${c.email}`} className="text-gold-300 hover:text-gold-200 inline-flex items-center gap-1.5"><Mail className="h-3 w-3" /> {c.email}</a>}
          </div>
        </div>
        <button onClick={() => window.confirm(`Delete ${c.name} and their call log?`) && onDelete(c.id)}
          data-testid={`contact-del-${c.id}`} className="text-white/20 hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-3 pt-3 border-t border-white/5">
        <button onClick={() => setOpen((o) => !o)} data-testid={`contact-notes-toggle-${c.id}`}
          className="text-gold-400/80 hover:text-gold-300 text-xs inline-flex items-center gap-1.5">
          <MessageSquarePlus className="h-3.5 w-3.5" /> Call log ({(c.notes || []).length})
        </button>
        {open && (
          <div className="mt-3 space-y-2">
            {(c.notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="border-l-2 border-gold-500/30 pl-3" data-testid={`contact-note-${n.id}`}>
                <p className="text-white/70 text-xs font-light whitespace-pre-wrap">{n.text}</p>
                <Mono className="text-[0.45rem] text-white/30">{(n.at || "").slice(0, 16).replace("T", " ")} · {n.by}</Mono>
              </div>
            ))}
            <form onSubmit={addNote} className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call or note…"
                className={inp} data-testid={`contact-note-input-${c.id}`} />
              <button type="submit" disabled={busy || !note.trim()} data-testid={`contact-note-submit-${c.id}`}
                className="shrink-0 border border-gold-500/40 text-gold-300 px-4 text-xs hover:bg-gold-500/10 disabled:opacity-40">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Log"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Contacts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const load = () => api.get("/contacts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.contacts.filter((c) =>
      (cat === "all" || c.category === cat) &&
      (!needle || [c.name, c.organisation, c.role, c.email, c.phone, c.state].filter(Boolean).join(" ").toLowerCase().includes(needle)));
  }, [data, q, cat]);

  if (!["admin", "staff"].includes(user?.role)) {
    return (
      <div className="border border-gold-500/20 bg-navy-900/40 p-10 text-center text-white" data-testid="contacts-restricted">
        <Lock className="h-8 w-8 text-gold-500/50 mx-auto" />
        <p className="mt-4 font-light">The contact register is for MDAu team members only.</p>
      </div>
    );
  }

  const del = async (id) => { await api.delete(`/contacts/${id}`); load(); };
  const addNote = async (id, text) => { await api.post(`/contacts/${id}/notes`, { text }); await load(); };

  return (
    <div className="space-y-8 text-white" data-testid="contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">S-16 · Outreach register</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3 flex items-center gap-3"><BookUser className="h-7 w-7 text-gold-400" /> Contacts</h1>
          <p className="text-white/50 font-light mt-2 max-w-2xl text-sm">
            One register for every DFES, DPC, council and panel contact — with a timestamped call log so nothing lives in a spreadsheet.
          </p>
        </div>
        <AddForm onDone={load} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-3.5 w-3.5 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, council, state…"
            className={`${inp} pl-9`} data-testid="contacts-search" />
        </div>
        {["all", ...Object.keys(CAT_LABEL)].map((k) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`contacts-filter-${k}`}
            className={`border px-3 py-1.5 text-xs transition-colors ${cat === k ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/50 hover:text-gold-300"}`}>
            {k === "all" ? "All" : CAT_LABEL[k]}
          </button>
        ))}
      </div>

      {!data ? <Loader2 className="h-5 w-5 animate-spin text-gold-400" /> : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="contacts-list">
          {filtered.map((c) => <ContactCard key={c.id} c={c} onDelete={del} onNote={addNote} />)}
          {filtered.length === 0 && (
            <p className="text-white/35 font-light text-sm col-span-full border border-dashed border-white/10 p-8 text-center">No contacts match.</p>
          )}
        </div>
      )}
    </div>
  );
}
